import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow
} from '../ui/table';
import { Package } from 'lucide-react';
import { PartNoCell } from '../shared/PartNoCell';
import { priceBasisLabel } from '../quotations/constants';
import { computeLineItemTotals, formatQtyTotal, formatWeightTotal } from '../../utils/lineItemTotals';

export function OrderItemsTable({ order, formatCurrency }) {
  const hasRemarks = (order.items || []).some(it => (it.remarks || '').trim().length > 0);
  const itemTotals = computeLineItemTotals(order.items || []);
  return (
    <>
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="text-lg font-heading uppercase flex items-center gap-2">
            <Package className="h-5 w-5" />
            Order Items ({order.items?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Part No</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead>UOM</TableHead>
                <TableHead className="text-right">Unit Price</TableHead>
                <TableHead>Per</TableHead>
                <TableHead className="text-right">Line Total</TableHead>
                {hasRemarks && <TableHead>Remarks</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(order.items || []).map((item, idx) => {
                return (
                <TableRow key={item.id || idx}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell className="font-semibold">
                    <PartNoCell
                      customerPartNo={item.customer_part_no}
                      internalPartNo={item.part_internal_no}
                    />
                  </TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell className="text-right">{(item.quantity || 0).toLocaleString()}</TableCell>
                  <TableCell>{item.uom === 'kgs' ? 'Kg' : 'Pcs'}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(item.unit_price, order.currency)}</TableCell>
                  <TableCell className="font-mono text-muted-foreground">
                    /{priceBasisLabel(item)}
                  </TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(item.line_total, order.currency)}</TableCell>
                  {hasRemarks && (
                    <TableCell className="text-xs text-slate-600 max-w-[240px] whitespace-pre-wrap" data-testid={`order-item-remarks-${idx}`}>
                      {item.remarks || ''}
                    </TableCell>
                  )}
                </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow className="bg-muted/60 font-semibold">
                <TableCell colSpan={3}>TOTAL ({itemTotals.count} item{itemTotals.count === 1 ? '' : 's'})</TableCell>
                <TableCell className="text-right font-mono" data-testid="order-items-total-qty">
                  {formatQtyTotal(itemTotals)}
                  {formatWeightTotal(itemTotals) !== '-' && (
                    <span className="block text-xs font-normal text-muted-foreground" data-testid="order-items-total-weight">
                      {formatWeightTotal(itemTotals)} Kg total wt
                    </span>
                  )}
                </TableCell>
                <TableCell colSpan={3} />
                <TableCell className="text-right font-mono" data-testid="order-items-total-amount">
                  {formatCurrency(itemTotals.lineTotal, order.currency)}
                </TableCell>
                {hasRemarks && <TableCell />}
              </TableRow>
            </TableFooter>
          </Table>
        </CardContent>
      </Card>

      <Card className="card-dashboard">
        <CardContent className="pt-6">
          <div className="flex justify-end">
            <div className="space-y-2 w-64">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span className="font-mono">{formatCurrency(order.subtotal, order.currency)}</span>
              </div>
              {(order.tooling_total || 0) > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Tooling:</span>
                  <span className="font-mono">{formatCurrency(order.tooling_total, order.currency)}</span>
                </div>
              )}
              {(order.sample_total || 0) > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Samples:</span>
                  <span className="font-mono">{formatCurrency(order.sample_total, order.currency)}</span>
                </div>
              )}
              {(order.handling_charge || 0) > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Handling:</span>
                  <span className="font-mono">{formatCurrency(order.handling_charge, order.currency)}</span>
                </div>
              )}
              {(order.tax_percentage || 0) > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Tax ({order.tax_percentage}%):</span>
                  <span className="font-mono">{formatCurrency(order.tax_amount, order.currency)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total:</span>
                <span className="font-mono text-primary">{formatCurrency(order.total_amount, order.currency)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
